import { ResourceHelperService } from "../resource-helper.service";

/**
 * A dataservice that provides CRUD operations on Scheme entries against the backend Scheme service
 */
export class SchemeService {
  public currentPage = 1;
  public perPageCount = 15;
  private resource: InterACT.Interfaces.IResource<InterACT.Interfaces.Schemes.SchemeResponse | InterACT.Interfaces.Schemes.SchemesResponse>;
  private schemeAssociationResource: InterACT.Interfaces.IResource<InterACT.Interfaces.SchemeAssociationRules.SchemeAssociationRuleResponse | InterACT.Interfaces.SchemeAssociationRules.SchemeAssociationRulesResponse>;

  constructor(private $resource: ng.resource.IResourceService, private resourceHelperService: ResourceHelperService) {
    this.resource = $resource('api/schemes/:schemeId', undefined, { edit: { method: 'PATCH' }, update: { method: 'PUT' } }, { stripTrailingSlashes: true });
    this.schemeAssociationResource = $resource('api/scheme-association-rules/:ruleId', undefined, {
      save: {
        method: 'POST',
        headers: {
          'Access-Control-Expose-Headers': 'Location'
        },
        transformResponse: (data, headers) => {
          const response: any = {};
          response.data = data;
          (<any>response).headers = headers();
          return response;
        }
      }
    });
  }

  /**
   * Function to retrieve records from the schemes backend service
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param searchText The search text used to build up a query string in the outgoing http request
   *
   * @returns An array of schemes
  */
  public getList(page?: number, perPageCount?: number, searchText?: string) {
    if (!page) {
      page = 1;
    }

    if (!perPageCount) {
      perPageCount = this.perPageCount;
    }

    this.perPageCount = perPageCount;
    this.currentPage = page;

    const options: any = {
      offset: (page - 1) * perPageCount,
      limit: perPageCount
    };

    if (angular.isDefined(searchText)) {
      options.q = searchText;
    }

    return this.resource.get(options).$promise.then((resource: InterACT.Interfaces.Schemes.SchemesResponse) => {
      return resource.schemes;
    });
  }

  /**
   * Function to retrieve a single record from the schemes backend service
   *
   * @param schemeId The id of the scheme we wish to receive
   *
   * @returns A Http promise resource object
  */
  public getSingleScheme(schemeId: string) {
    return this.resource.get({ schemeId: schemeId }).$promise;
  }

  /**
   * Function to add products to a scheme
   *
   * @param rule The product rule we wish to add to the scheme
   *
   * @returns A Http promise resource object
  */
  public associateProductsWithScheme(rule: InterACT.Interfaces.SchemeAssociationRules.SchemeAssociationRule) {
    return this.schemeAssociationResource.save(rule).$promise;
  }

  /**
   * Function to remove a rule from a scheme
   *
   * @param ruleId The id of the scheme we wish to remove from the scheme
   *
   * @returns A Http promise resource object
  */
  public removeRuleFromScheme(ruleId: string) {
    return this.schemeAssociationResource.delete({ ruleId: ruleId }).$promise;
  }

  /**
   * Function to get a list of associated rules for a selected scheme
   *
   * @param query the query used to search for associated rules
   *
   * @returns A Http promise resource object
  */
  public getAssociatedRules(query: string) {
    const options = this.resourceHelperService.buildRequestOptions(undefined, undefined, query);
    return this.schemeAssociationResource.get(options).$promise;
  }
}
